import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/drama" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

interface SiteHeaderProps {
  overlay?: boolean;
}

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !overlay;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          solid
            ? "bg-background-50/95 backdrop-blur border-b border-background-200"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="h-16 md:h-20 px-4 md:px-8 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center">
              <i className={`ri-movie-2-line text-lg ${solid ? "text-background-50" : "text-background-50"}`}></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading font-black text-lg tracking-wide ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                缘之空
              </span>
              <span
                className={`text-[10px] tracking-[0.2em] ${
                  solid ? "text-foreground-500" : "text-background-200"
                }`}
              >
                YUANZHIKONG
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? solid
                        ? "text-primary-500 bg-primary-50"
                        : "text-background-50 bg-background-50/20"
                      : solid
                        ? "text-foreground-700 hover:text-primary-500 hover:bg-background-100"
                        : "text-background-100 hover:text-background-50 hover:bg-background-50/15"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/search"
              aria-label="搜索影视"
              className={`hidden md:flex w-10 h-10 items-center justify-center rounded-md transition-colors ${
                solid
                  ? "text-foreground-700 hover:bg-background-100 hover:text-primary-500"
                  : "text-background-50 hover:bg-background-50/15"
              }`}
            >
              <i className="ri-search-line text-lg"></i>
            </Link>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen(true)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                solid
                  ? "text-foreground-900 hover:bg-background-100"
                  : "text-background-50 hover:bg-background-50/15"
              }`}
            >
              <i className="ri-menu-3-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-foreground-950/50"
          onClick={() => setMenuOpen(false)}
        ></div>
        <aside
          className={`absolute top-0 left-0 h-full w-72 max-w-[80vw] bg-background-50 flex flex-col transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="h-16 px-5 flex items-center justify-between border-b border-background-200">
            <span className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-md bg-primary-500 flex items-center justify-center">
                <i className="ri-movie-2-line text-background-50"></i>
              </span>
              <span className="font-heading font-black text-foreground-950">缘之空</span>
            </span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setMenuOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <nav className="flex flex-col p-4 gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-base font-medium transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-500"
                      : "text-foreground-800 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto p-5 text-xs text-foreground-500 border-t border-background-200">
            <p className="font-medium text-foreground-800 mb-1">缘之空 · 高清影视内容站</p>
            <p>专注影视资源展示与剧情解析</p>
          </div>
        </aside>
      </div>
    </>
  );
}